/**
 * Featured-carousel layout + visual constants. Vertical embla axis across
 * all three variants; only sizing differs.
 *
 * The scale tween in `FeaturedCarousel` interpolates each slide from
 * `minScale = adjacentSize / featuredSize` up to 1.0 as it approaches the
 * centered snap. `inactiveFilter`'s blur radius is the *max* applied at
 * `t = 0` and is interpolated down to 0 for the centered slide.
 */

export type CarouselConfig = {
  /** Slide size (px) at scale = 1. Slots are square. */
  featuredSize: number;
  /** Visual size (px) when fully tweened away from center. */
  adjacentSize: number;
  /** Vertical gap between slides (px). */
  slideSpacing: number;
  /** Viewport cross-axis (width). */
  viewportWidth: number;
  /** Viewport main axis (height). Controls peek above/below the active slide. */
  viewportHeight: number;
  /** Max CSS filter at `t = 0`; aim for `blur(~5% of adjacentSize)`. */
  inactiveFilter: string;
};

export const CAROUSEL: CarouselConfig = {
  featuredSize: 127,
  adjacentSize: 100,
  slideSpacing: 11.5,
  // Cross-axis width — needs ≥ ~198px so the active halo's `blur(16px)`
  // Gaussian falloff isn't clipped by the viewport's `overflow-hidden`.
  viewportWidth: 200,
  viewportHeight: 188,
  inactiveFilter: "blur(5px) saturate(1.3)",
};

/** Phone-width examples-sheet preset. */
export const MOBILE_CAROUSEL: CarouselConfig = {
  featuredSize: 200,
  adjacentSize: 158,
  slideSpacing: 15,
  viewportWidth: 343,
  // Sized to peek a thin strip of the adjacent thumbs above/below the featured one.
  viewportHeight: 290,
  // 8px (~5% of 158px adjacent) — matches desktop's blur-to-thumb ratio.
  inactiveFilter: "blur(8px) saturate(1.3)",
};

/** Inline preview below the prompt input on phones. */
export const COMPACT_CAROUSEL: CarouselConfig = {
  featuredSize: 67,
  // ~79% — matches CAROUSEL / MOBILE_CAROUSEL ratios so the scale tween
  // shrink reads proportionally the same across variants.
  adjacentSize: 53,
  slideSpacing: 6.25,
  // Wider than `featuredSize` so the `left-4` dots overlay in the
  // inline-compact layout has room beside the thumb, not on top of it.
  viewportWidth: 124,
  viewportHeight: 97,
  // ~5% of `adjacentSize` to match the desktop variant's peek-blur ratio.
  inactiveFilter: "blur(3px) saturate(1.3)",
};

/**
 * Tailwind's `xl` breakpoint. The carousel uses JS detection at this width to
 * pick between absolute-pixel-sized presets (`CAROUSEL` vs. `MOBILE_CAROUSEL`
 * / `COMPACT_CAROUSEL`). Components using purely cosmetic switches at the
 * same breakpoint should use Tailwind's `xl:` prefix so the JS- and
 * CSS-driven changes align.
 */
export const STACKED_LAYOUT_BREAKPOINT_PX = 1280;

/** Shared visual constants for slots + viewport overlays. */
export const SLOT_STYLE = {
  innerBorderColor: "rgba(35, 34, 32, 0.15)",
  activeBoxShadow: "0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)",
  gridThumbBoxShadow: "0 4px 12px 0 rgba(0, 0, 0, 0.25)",
  haloFilter: "blur(16px) saturate(2)",
  haloScale: 1.05,
  haloOpacity: 0.5,
  // Tracks `--background-alt-2` so the viewport-edge fade works in dark mode too.
  peekFadeColorStart: "color-mix(in srgb, var(--background-alt-2) 90%, transparent)",
  peekFadeColorEnd: "transparent",
} as const;

/** Pulls the numeric blur radius out of e.g. `"blur(5px) saturate(1.3)"`. */
export const parseMaxBlurPx = (filter: string): number => {
  const match = filter.match(/blur\(([\d.]+)px\)/);
  return match ? parseFloat(match[1]!) : 0;
};
